import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { aboutStats, aboutMilestones, faqData } from "@/mocks/sportsData";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9rdd8knlsngrm4lmajg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setFormError("提交失败，请稍后再试。");
          setFormStatus("error");
        } else {
          setFormError(serverMsg || "提交失败，请稍后再试。");
          setFormStatus("error");
        }
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试。");
      setFormStatus("error");
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "极速体育",
    url: "https://www.jisusports.com/",
    description: "极速体育——实时赛事直播与体育资讯平台",
    foundingDate: "2018",
    address: {
      "@type": "PostalAddress",
      streetAddress: "体育中心路88号",
      addressLocality: "北京",
      addressRegion: "北京",
      postalCode: "100000",
      addressCountry: "CN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+86-400-888-8888",
      contactType: "customer service",
      areaServed: "CN",
      availableLanguage: "Chinese",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.jisusports.com/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://www.jisusports.com/about" },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 极速体育"
        description="极速体育成立于2018年，是国内领先的实时赛事直播与体育资讯平台。覆盖全球200+赛事，服务580万+日活用户，致力于为体育迷提供最极速、最专业的体育内容服务。"
        keywords="关于极速体育,极速体育品牌,体育直播平台,体育资讯平台"
        canonicalUrl="https://www.jisusports.com/about"
        structuredData={{ ...orgSchema, ...breadcrumbSchema, ...faqSchema }}
      />

      <main className="pt-20">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于极速体育
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              极速体育成立于2018年，致力于为全球体育迷打造最快速、最专业、最全面的体育内容平台。
              覆盖足球、篮球、网球、电竞等全运动品类，让你零延迟感受每一个精彩瞬间。
            </p>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
            {aboutStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-background-200/70 hidden md:block"></div>
              <div className="space-y-8">
                {aboutMilestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`flex flex-col md:flex-row items-start gap-6 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                      <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5">
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded-full mb-2">
                          {milestone.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">{milestone.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-10 justify-center flex-shrink-0">
                      <div className="w-4 h-4 rounded-full bg-primary-500 border-4 border-background-100 mt-6"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如果您有任何问题或建议，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">客服电话</div>
                <div className="text-xs text-foreground-500">400-888-8888</div>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</div>
                <div className="text-xs text-foreground-500">support@jisusports.com</div>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div className="text-sm font-semibold text-foreground-900 mb-1">办公地址</div>
                <div className="text-xs text-foreground-500">北京市朝阳区体育中心路88号</div>
              </div>
            </div>

            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

              {formStatus === "success" && (
                <div className="mb-6 p-4 bg-accent-100 text-accent-700 rounded-xl text-sm flex items-center gap-2">
                  <i className="ri-check-line text-lg"></i>
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}

              {formStatus === "error" && (
                <div className="mb-6 p-4 bg-primary-100 text-primary-700 rounded-xl text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  {formError || "提交失败，请稍后再试。"}
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form="true" id="about-contact-form">
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}